import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useRecoilValue } from "recoil";
import organizationIdSelector from "../../atoms/organizationIdSelector";
import {
  ADD_CLIENT_REQUEST_EXAMPLE,
  ADD_CLIENT_SUCCESS_EXAMPLE,
  API_SAMPLE_DOWNLOADS,
  buildAddClientCurlSample,
  buildAddClientNodeSample,
  buildAddClientPythonSample,
  buildClientSearchCurlSample,
  buildClientSummaryCurlSample,
  buildOrgApiSkillSample,
  CLIENT_SEARCH_RESPONSE_EXAMPLE,
  CLIENT_SUMMARY_SECTIONS,
  CLIENT_SUMMARY_DETAIL_LEVELS,
  MIGHTY45_API_BASE_URL,
} from "../../data/organizationApiDocs";

const SAMPLE_TABS = [
  { id: "curl", label: "curl" },
  { id: "node", label: "Node.js" },
  { id: "python", label: "Python" },
];

function CodeBlock({ code, onCopy, maxHeight }) {
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "grey.900",
        color: "grey.100",
        borderRadius: 1,
        p: 2,
        pr: 6,
        overflow: "auto",
        maxHeight: maxHeight || "none",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: "pre",
      }}
    >
      <Tooltip title="Copy">
        <IconButton
          size="small"
          onClick={() => onCopy(code)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "grey.300",
          }}
          aria-label="Copy code sample"
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {code}
    </Box>
  );
}

function InfoRow({ label, value, onCopy }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="code"
        sx={{
          bgcolor: "grey.100",
          px: 1,
          py: 0.5,
          borderRadius: 0.5,
          wordBreak: "break-all",
        }}
      >
        {value}
      </Typography>
      {onCopy ? (
        <Tooltip title={`Copy ${label}`}>
          <IconButton size="small" onClick={() => onCopy(value)} aria-label={`Copy ${label}`}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

const APIDocumentation = ({ apiKeyPreview }) => {
  const organizationId = useRecoilValue(organizationIdSelector);
  const [sampleTab, setSampleTab] = React.useState("curl");
  const [copyMessage, setCopyMessage] = React.useState("");

  const sampleParams = React.useMemo(
    () => ({
      organizationId: organizationId || "YOUR_ORGANIZATION_ID",
      apiKey: apiKeyPreview || "YOUR_API_KEY",
      coachId: "COACH_USER_UID",
    }),
    [organizationId, apiKeyPreview],
  );

  const sampleCode = React.useMemo(() => {
    if (sampleTab === "node") {
      return buildAddClientNodeSample(sampleParams);
    }
    if (sampleTab === "python") {
      return buildAddClientPythonSample(sampleParams);
    }
    return buildAddClientCurlSample(sampleParams);
  }, [sampleTab, sampleParams]);

  const agentSkillSample = React.useMemo(
    () =>
      buildOrgApiSkillSample({
        organizationId: organizationId || "YOUR_ORGANIZATION_ID",
        apiKey: apiKeyPreview || "YOUR_API_KEY",
      }),
    [organizationId, apiKeyPreview],
  );

  const handleCopy = React.useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage("Copied to clipboard");
    } catch {
      setCopyMessage("Could not copy — select the text manually");
    }
  }, []);

  React.useEffect(() => {
    if (!copyMessage) return undefined;
    const timer = window.setTimeout(() => setCopyMessage(""), 2500);
    return () => window.clearTimeout(timer);
  }, [copyMessage]);

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="h4" sx={{ mb: 1 }}>
        API documentation
      </Typography>
      <Typography sx={{ mb: 2, color: "text.secondary" }}>
        Use your organization API key to search clients and fetch read-only
        analytics summaries for AI agents and integrations. Keys authenticate
        as your organization — not as an individual coach login. Rate limits:
        120 requests/minute per key and 5,000 requests/day per organization.
      </Typography>

      {copyMessage ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {copyMessage}
        </Alert>
      ) : null}

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <InfoRow label="Base URL" value={MIGHTY45_API_BASE_URL} onCopy={handleCopy} />
        <InfoRow
          label="Organization ID"
          value={organizationId || "Loading…"}
          onCopy={organizationId ? handleCopy : undefined}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
            Required headers
          </Typography>
          <Chip size="small" label="Authorization: Bearer &lt;API key&gt;" />
          <Chip size="small" label="organizationid: &lt;org id&gt;" />
          <Chip size="small" label="Content-Type: application/json" />
        </Stack>
      </Stack>

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label="GET" size="small" color="success" />
            <Typography fontWeight={600}>/api/v1</Typography>
            <Typography variant="body2" color="text.secondary">
              Health check (no auth)
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Verify the API is reachable before sending authenticated requests.
          </Typography>
          <CodeBlock
            code={`curl -sS "${MIGHTY45_API_BASE_URL}"`}
            onCopy={handleCopy}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label="GET" size="small" color="success" />
            <Typography fontWeight={600}>/api/v1/org/clients</Typography>
            <Typography variant="body2" color="text.secondary">
              Search clients by name or email
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Query params: <code>q</code> (required, min 2 chars), <code>limit</code> (default 25, max 50).
          </Typography>
          <CodeBlock
            code={buildClientSearchCurlSample({
              organizationId: organizationId || "YOUR_ORGANIZATION_ID",
              apiKey: apiKeyPreview || "YOUR_API_KEY",
            })}
            onCopy={handleCopy}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Response example
          </Typography>
          <CodeBlock code={CLIENT_SEARCH_RESPONSE_EXAMPLE} onCopy={handleCopy} />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label="GET" size="small" color="success" />
            <Typography fontWeight={600}>/api/v1/org/clients/:clientId/summary</Typography>
            <Typography variant="body2" color="text.secondary">
              Full read-only client analytics
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Returns profile, training plan, nutrition goals, BigQuery analytics,
            recent health/workout activity, weekly reports, progress photos,
            coach logs, and workout trends. Optional query params:{" "}
            <code>detail</code> (<code>summary</code> default, <code>full</code>{" "}
            for drill-down), <code>healthDays</code> (7–365),{" "}
            <code>exerciseLimit</code> (5–100).
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>detail=summary</strong> — {CLIENT_SUMMARY_DETAIL_LEVELS.summary}{" "}
            <strong>detail=full</strong> — {CLIENT_SUMMARY_DETAIL_LEVELS.full}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sections: {CLIENT_SUMMARY_SECTIONS.join(", ")}
          </Typography>
          <CodeBlock
            code={buildClientSummaryCurlSample({
              organizationId: organizationId || "YOUR_ORGANIZATION_ID",
              apiKey: apiKeyPreview || "YOUR_API_KEY",
            })}
            onCopy={handleCopy}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label="POST" size="small" color="primary" />
            <Typography fontWeight={600}>/api/v1/add_client</Typography>
            <Typography variant="body2" color="text.secondary">
              Create a client
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Creates a client user and organization client record with a connect
            code for mobile onboarding. Requires <strong>firstName</strong>,{" "}
            <strong>lastName</strong>, and <strong>coachId</strong> (coach UID
            from the Coaches page).
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Request body example
          </Typography>
          <CodeBlock code={ADD_CLIENT_REQUEST_EXAMPLE} onCopy={handleCopy} />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Success response (200)
          </Typography>
          <CodeBlock code={ADD_CLIENT_SUCCESS_EXAMPLE} onCopy={handleCopy} />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Errors return HTTP 400 with plain text (for example{" "}
            <code>Invalid API Key.</code> or{" "}
            <code>Missing required first or last name or coachId.</code>).
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Code samples
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Samples below use your organization ID when available. Replace{" "}
        <code>YOUR_API_KEY</code> and <code>COACH_USER_UID</code> before running.
        {apiKeyPreview ? " The preview uses your most recently listed key." : " Create an API key above first."}
      </Typography>

      <Tabs
        value={sampleTab}
        onChange={(_e, value) => setSampleTab(value)}
        sx={{ mb: 2 }}
      >
        {SAMPLE_TABS.map((tab) => (
          <Tab key={tab.id} value={tab.id} label={tab.label} />
        ))}
      </Tabs>
      <CodeBlock code={sampleCode} onCopy={handleCopy} />

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2, mb: 3 }}>
        {API_SAMPLE_DOWNLOADS.map((item) => (
          <Button
            key={item.href}
            component="a"
            href={item.href}
            download
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          border: "1px dashed",
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <SmartToyIcon color="secondary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            AI agent skill (copy and paste)
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Paste this into a file named <code>SKILL.md</code> for Cursor or
          another AI coding agent — for example{" "}
          <code>~/.cursor/skills/mighty45-org-api/SKILL.md</code> (personal) or{" "}
          <code>.cursor/skills/mighty45-org-api/SKILL.md</code> in your project.
          Your organization ID is filled in below when available; replace{" "}
          <code>YOUR_API_KEY</code> and <code>COACH_USER_UID</code> with real
          values before sharing the file.
        </Typography>
        <CodeBlock
          code={agentSkillSample}
          onCopy={handleCopy}
          maxHeight={420}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<ContentCopyIcon />}
            onClick={() => handleCopy(agentSkillSample)}
          >
            Copy full skill
          </Button>
          <Button
            component="a"
            href="/api-docs/mighty45-org-api-SKILL.md"
            download
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Download SKILL.md
          </Button>
          <Button
            component={Link}
            href="/api-docs/mighty45-organization-api.md"
            download
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Download API reference
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default APIDocumentation;
